import type { ShopClip } from '../types/shop.types'
import { parseShopBusiness, parseShopProduct } from './shop-product.model'

type Dict = Record<string, unknown>

const isDict = (value: unknown): value is Dict =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const str = (value: unknown): string | null => (typeof value === 'string' && value !== '' ? value : null)
const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/**
 * One row of the Shop's public clips API, or `null` when it lacks the two
 * fields a card cannot do without: its id and its link into the Shop.
 */
export function parseShopClip(value: unknown): ShopClip | null {
  if (!isDict(value)) return null
  const id = str(value.id)
  const clipUrl = str(value.clipUrl)
  if (!id || !clipUrl) return null
  return {
    id,
    caption: str(value.caption),
    creatorHandle: str(value.creatorHandle),
    durationSeconds: num(value.durationSeconds),
    views: num(value.views) ?? 0,
    shares: num(value.shares) ?? 0,
    videoUrl: str(value.videoUrl),
    posterUrl: str(value.posterUrl),
    business: parseShopBusiness(value.business),
    product: parseShopProduct(value.product),
    clipUrl,
    shareUrl: str(value.shareUrl),
  }
}

/** The `{ clips: [...] }` envelope the clips endpoint returns. */
export function parseShopClips(payload: unknown): ShopClip[] {
  if (!isDict(payload) || !Array.isArray(payload.clips)) return []
  return payload.clips.map(parseShopClip).filter((clip): clip is ShopClip => clip !== null)
}

/** The line a clip card shows: its caption, else its product, else its store. */
export function shopClipTitle(clip: ShopClip): string {
  return clip.caption ?? clip.product?.title ?? clip.business?.name ?? 'Clip'
}
